import * as THREE from 'three';
import { CONFIG } from '../config.js';

/**
 * Custom atmospheric sky dome with Rayleigh-inspired blue gradient and
 * Mie-inspired sun glow. Outputs LDR values directly — no tone mapping needed.
 *
 * All scattering math is loosely based on Preetham/O'Neil models but deliberately
 * scaled to produce 0-1 output. This avoids the HDR nightmare of the stock
 * Three.js Sky shader.
 */
export class Sky {
    /** @type {THREE.Mesh} */
    #sky;

    /** @type {THREE.Vector3} */
    #sunPosition;

    /** @type {number} */
    #elevation;

    /** @type {number} */
    #azimuth;

    constructor() {
        this.#sunPosition = new THREE.Vector3();
        this.#elevation = CONFIG.sky.elevation;
        this.#azimuth = CONFIG.sky.azimuth;
        this.#sky = this.#createSky();
        this.#updateSunPosition();
    }

    /**
     * Get the sky mesh
     * @returns {THREE.Mesh}
     */
    get mesh() {
        return this.#sky;
    }

    /**
     * Get the sun position vector (normalized direction)
     * @returns {THREE.Vector3}
     */
    get sunPosition() {
        return this.#sunPosition;
    }

    /**
     * Create the sky dome with custom atmospheric shader
     * @returns {THREE.Mesh}
     */
    #createSky() {
        const geometry = new THREE.SphereGeometry(900, 32, 15);
        const material = new THREE.ShaderMaterial({
            uniforms: {
                sunPosition: { value: new THREE.Vector3() },
                turbidity: { value: CONFIG.sky.turbidity },
                rayleigh: { value: CONFIG.sky.rayleigh },
                mieCoefficient: { value: CONFIG.sky.mieCoefficient },
                mieDirectionalG: { value: CONFIG.sky.mieDirectionalG },
            },
            vertexShader: /* glsl */ `
                varying vec3 vWorldDirection;

                void main() {
                    vec4 worldPos = modelMatrix * vec4(position, 1.0);
                    vWorldDirection = normalize(worldPos.xyz);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                }
            `,
            fragmentShader: /* glsl */ `
                uniform vec3 sunPosition;
                uniform float turbidity;
                uniform float rayleigh;
                uniform float mieCoefficient;
                uniform float mieDirectionalG;

                varying vec3 vWorldDirection;

                // Rayleigh scattering coefficients (wavelength-dependent blue bias)
                const vec3 rayleighCoeff = vec3(5.5e-6, 13.0e-6, 22.4e-6);

                // Henyey-Greenstein phase function for Mie scattering
                float hgPhase(float cosTheta, float g) {
                    float g2 = g * g;
                    float denom = 1.0 + g2 - 2.0 * g * cosTheta;
                    return (1.0 - g2) / (4.0 * 3.14159265 * pow(denom, 1.5));
                }

                void main() {
                    vec3 dir = normalize(vWorldDirection);
                    vec3 sunDir = normalize(sunPosition);

                    // Height above horizon: 0 at horizon, 1 at zenith
                    float elevation = max(dir.y, 0.0);

                    // Optical depth — thicker atmosphere near horizon
                    float zenithAngle = acos(max(elevation, 0.01));
                    float opticalDepth = 1.0 / (cos(zenithAngle) + 0.15 * pow(93.885 - degrees(zenithAngle), -1.253));
                    // Clamp to prevent insane values at horizon
                    opticalDepth = min(opticalDepth, 40.0);

                    // Turbidity scales the overall haze
                    float hazeDepth = opticalDepth * turbidity * 0.04;

                    // --- Rayleigh scattering (blue sky) ---
                    vec3 rayleighColor = rayleighCoeff * rayleigh * opticalDepth * 800.0;
                    // Normalize to 0-1 range
                    rayleighColor = 1.0 - exp(-rayleighColor);

                    // --- Base sky gradient ---
                    // Deep blue at zenith, lighter toward horizon
                    vec3 zenithColor = vec3(0.15, 0.35, 0.65);
                    vec3 horizonColor = vec3(0.55, 0.70, 0.85);

                    // Mix Rayleigh blue with base gradient
                    float heightMix = pow(elevation, 0.4);
                    vec3 skyColor = mix(horizonColor, zenithColor, heightMix);
                    skyColor = mix(skyColor, rayleighColor, 0.6);

                    // --- Atmospheric haze (turbidity) ---
                    vec3 hazeColor = vec3(0.75, 0.78, 0.82);
                    float hazeFactor = 1.0 - exp(-hazeDepth);
                    hazeFactor = clamp(hazeFactor, 0.0, 0.6);
                    skyColor = mix(skyColor, hazeColor, hazeFactor);

                    // --- Sun angle influence ---
                    // Warm shift when sun is low
                    float sunHeight = max(sunDir.y, 0.0);
                    float warmth = 1.0 - smoothstep(0.0, 0.5, sunHeight);
                    vec3 warmTint = vec3(1.0, 0.6, 0.3);
                    skyColor = mix(skyColor, skyColor * warmTint, warmth * 0.4);

                    // --- Mie scattering (sun glow) ---
                    float cosTheta = dot(dir, sunDir);
                    float miePhase = hgPhase(cosTheta, mieDirectionalG);
                    float mieStrength = mieCoefficient * miePhase * 30.0;
                    mieStrength = clamp(mieStrength, 0.0, 1.0);

                    // Sun glow color — warm white near the sun
                    vec3 sunGlowColor = mix(vec3(1.0, 0.95, 0.8), vec3(1.0, 0.5, 0.2), warmth * 0.6);
                    skyColor = mix(skyColor, sunGlowColor, mieStrength);

                    // --- Horizon brightening ---
                    float horizonGlow = 1.0 - smoothstep(0.0, 0.15, elevation);
                    vec3 horizonGlowColor = mix(vec3(0.7, 0.75, 0.85), vec3(0.9, 0.6, 0.35), warmth);
                    skyColor = mix(skyColor, horizonGlowColor, horizonGlow * 0.4);

                    // --- Below horizon: darken to ground ---
                    float belowHorizon = smoothstep(0.0, -0.05, dir.y);
                    vec3 groundColor = vec3(0.25, 0.3, 0.2);
                    skyColor = mix(skyColor, groundColor, belowHorizon);

                    gl_FragColor = vec4(clamp(skyColor, 0.0, 1.0), 1.0);
                }
            `,
            side: THREE.BackSide,
            depthWrite: false,
        });

        return new THREE.Mesh(geometry, material);
    }

    /**
     * Update sun position from elevation and azimuth
     */
    #updateSunPosition() {
        const phi = THREE.MathUtils.degToRad(90 - this.#elevation);
        const theta = THREE.MathUtils.degToRad(this.#azimuth);

        this.#sunPosition.setFromSphericalCoords(1, phi, theta);

        const mat = /** @type {THREE.ShaderMaterial} */ (this.#sky.material);
        mat.uniforms['sunPosition'].value.copy(this.#sunPosition);
    }

    /**
     * Set sky scattering parameters
     * @param {{ turbidity: number, rayleigh: number, mieCoefficient: number, mieDirectionalG: number }} values
     */
    setScattering(values) {
        const mat = /** @type {THREE.ShaderMaterial} */ (this.#sky.material);
        mat.uniforms['turbidity'].value = values.turbidity;
        mat.uniforms['rayleigh'].value = values.rayleigh;
        mat.uniforms['mieCoefficient'].value = values.mieCoefficient;
        mat.uniforms['mieDirectionalG'].value = values.mieDirectionalG;
    }

    /**
     * Get sky scattering parameters
     * @returns {{ turbidity: number, rayleigh: number, mieCoefficient: number, mieDirectionalG: number }}
     */
    getScattering() {
        const mat = /** @type {THREE.ShaderMaterial} */ (this.#sky.material);
        return {
            turbidity: mat.uniforms['turbidity'].value,
            rayleigh: mat.uniforms['rayleigh'].value,
            mieCoefficient: mat.uniforms['mieCoefficient'].value,
            mieDirectionalG: mat.uniforms['mieDirectionalG'].value,
        };
    }

    /**
     * Set sun elevation in degrees (0-90)
     * @param {number} elevation
     */
    setElevation(elevation) {
        this.#elevation = elevation;
        this.#updateSunPosition();
    }

    /**
     * Set sun azimuth in degrees (0-360)
     * @param {number} azimuth
     */
    setAzimuth(azimuth) {
        this.#azimuth = azimuth;
        this.#updateSunPosition();
    }

    /**
     * Set both elevation and azimuth at once
     * @param {number} elevation - Elevation in degrees (0-90)
     * @param {number} azimuth - Azimuth in degrees (0-360)
     */
    setSunAngles(elevation, azimuth) {
        this.#elevation = elevation;
        this.#azimuth = azimuth;
        this.#updateSunPosition();
    }

    /**
     * Get sun angles
     * @returns {{ elevation: number, azimuth: number }}
     */
    getSunAngles() {
        return {
            elevation: this.#elevation,
            azimuth: this.#azimuth,
        };
    }

    /**
     * Dispose of sky resources
     */
    dispose() {
        this.#sky.geometry.dispose();
        const mat = /** @type {THREE.ShaderMaterial} */ (this.#sky.material);
        mat.dispose();
    }
}
